/**
 * 深度合并两个翻译对象
 * @param target - 目标对象（优先级较低，会被覆盖）
 * @param source - 源对象（优先级较高，会覆盖目标对象）
 * @returns 合并后的对象
 *
 * @example
 * const shared = { Common: { submit: "Submit" } }
 * const local = { Common: { submit: "Send" } }
 * const result = deepMerge(shared, local)
 * // result = { Common: { submit: "Send" } } // local 覆盖了 shared
 */
export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target }

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key]
      const targetValue = result[key]

      // 如果两者都是对象（且不是数组），递归合并
      if (
        sourceValue &&
        typeof sourceValue === "object" &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === "object" &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(targetValue, sourceValue)
      } else {
        // 否则直接用 source 的值覆盖（source 优先）
        result[key] = sourceValue as any
      }
    }
  }

  return result
}

/**
 * 合并共享翻译和本地翻译
 * 本地翻译会覆盖共享翻译中的同名键
 *
 * @param sharedMessages - 共享翻译对象（来自 @repo/i18n）
 * @param localMessages - 本地翻译对象（来自 App 的 messages 目录）
 * @returns 合并后的翻译对象
 *
 * @example
 * import sharedEn from '@repo/i18n/en'
 * import localEn from './messages/en.json'
 *
 * const messages = mergeMessages(sharedEn, localEn)
 */
export function mergeMessages<T extends Record<string, any>>(sharedMessages: T, localMessages: Partial<T>): T {
  // 本地翻译作为 source，会覆盖共享翻译
  return deepMerge(sharedMessages, localMessages)
}
